<template>
  <div class="pa-10" v-if="overview">
    <title-page icon="mdi-finance">
      {{ $t('billing-control.workspace') }}
    </title-page>

    <v-breadcrumbs
        :items="[
            {text: $t('reports.reports'), to: {name: 'reports'}, exact: true},
            {text: $t('billing-control.workspace')},
        ]"
        divider=">"
    ></v-breadcrumbs>

    <!-- Filters -->
    <v-row align="center">
      <v-col cols="12" md="3">
        <v-select
            :label="$t('period')"
            :items="yearItems"
            v-model="selectedYear"
            dense
            hide-details
            outlined
        >
          <template #append-outer><help-tip :text="$t('billing-control.help.period')"/></template>
        </v-select>
      </v-col>
      <v-col cols="12" md="4">
        <v-btn-toggle v-model="mode" mandatory dense color="primary">
          <v-btn value="cash" small>{{ $t('billing-control.mode.cash') }}</v-btn>
          <v-btn value="contract" small>{{ $t('billing-control.mode.contract') }}</v-btn>
        </v-btn-toggle>
        <help-tip :text="$t('billing-control.mode-hint')"/>
      </v-col>
    </v-row>

    <!-- KPIs -->
    <v-row class="mt-2">
      <v-col cols="12" md="4" lg="2">
        <dashboard-kpi-card :label="$t('billing-control.kpi.active-billable-licenses')" :value="overview.kpis.activeBillableLicenses" icon="mdi-account-multiple-check">
          <template #label-append><help-tip :text="$t('billing-control.help.kpi.active-billable-licenses')"/></template>
        </dashboard-kpi-card>
      </v-col>
      <v-col cols="12" md="4" lg="2">
        <dashboard-kpi-card :label="$t('billing-control.kpi.expected-revenue')" :value="numberFormat(overview.kpis.expectedRevenue)" unit="€" icon="mdi-cash-multiple">
          <template #label-append><help-tip :text="$t('billing-control.help.kpi.expected-revenue')"/></template>
        </dashboard-kpi-card>
      </v-col>
      <v-col cols="12" md="4" lg="2">
        <dashboard-kpi-card :label="$t('billing-control.kpi.already-billed')" :value="numberFormat(overview.kpis.alreadyBilled)" unit="€" icon="mdi-check-circle-outline">
          <template #label-append><help-tip :text="$t('billing-control.help.kpi.already-billed')"/></template>
        </dashboard-kpi-card>
      </v-col>
      <v-col cols="12" md="4" lg="2">
        <dashboard-kpi-card :label="$t('billing-control.kpi.pending-billing')" :value="numberFormat(overview.kpis.pendingBilling)" unit="€" icon="mdi-progress-clock" icon-color="#f59e0b">
          <template #label-append><help-tip :text="$t('billing-control.help.kpi.pending-billing')"/></template>
        </dashboard-kpi-card>
      </v-col>
      <v-col cols="12" md="4" lg="2">
        <dashboard-kpi-card :label="$t('billing-control.kpi.remaining-to-bill')" :value="numberFormat(overview.kpis.remainingToBill)" unit="€" icon="mdi-alert-circle-outline" danger>
          <template #label-append><help-tip :text="$t('billing-control.help.kpi.remaining-to-bill')"/></template>
        </dashboard-kpi-card>
      </v-col>
      <v-col cols="12" md="4" lg="2">
        <dashboard-kpi-card :label="$t('billing-control.kpi.capture-rate')" :value="capturePercent" unit="%" icon="mdi-percent-outline">
          <template #label-append><help-tip :text="$t('billing-control.help.kpi.capture-rate')"/></template>
        </dashboard-kpi-card>
      </v-col>
    </v-row>

    <!-- Revenue Leakage + Revenue Opportunities (separate concepts) -->
    <v-row>
      <v-col cols="12" lg="7">
        <v-card class="fill-height">
          <v-card-title class="text-subtitle-1">
            <v-icon left color="error">mdi-water-alert-outline</v-icon>
            {{ $t('billing-control.leakage.title') }}
            <help-tip :text="$t('billing-control.help.leakage.title')"/>
          </v-card-title>
          <v-card-text>
            <v-row dense>
              <v-col cols="6" md="3">
                <div class="caption text-uppercase grey--text">{{ $t('billing-control.leakage.total-missing') }} <help-tip :text="$t('billing-control.help.leakage.total-missing')"/></div>
                <div class="text-h6 error--text">{{ numberFormat(overview.revenueLeakage.totalMissing) }} €</div>
              </v-col>
              <v-col cols="6" md="3">
                <div class="caption text-uppercase grey--text">{{ $t('billing-control.leakage.recoverable') }} <help-tip :text="$t('billing-control.help.leakage.recoverable')"/></div>
                <div class="text-h6 success--text">{{ numberFormat(overview.revenueLeakage.recoverable) }} €</div>
              </v-col>
              <v-col cols="6" md="3">
                <div class="caption text-uppercase grey--text">{{ $t('billing-control.leakage.non-recoverable') }} <help-tip :text="$t('billing-control.help.leakage.non-recoverable')"/></div>
                <div class="text-h6">{{ numberFormat(overview.revenueLeakage.nonRecoverable) }} €</div>
              </v-col>
              <v-col cols="6" md="3">
                <div class="caption text-uppercase grey--text">{{ $t('billing-control.leakage.orgs-impacted') }} <help-tip :text="$t('billing-control.help.leakage.orgs-impacted')"/></div>
                <div class="text-h6">{{ overview.revenueLeakage.organizationsImpacted }}</div>
              </v-col>
            </v-row>

            <v-row dense class="mt-4">
              <v-col cols="4" v-for="tab in leakageTabs" :key="tab.key">
                <v-card outlined class="text-center pa-2" @click="goToGaps(tab.key)" style="cursor:pointer">
                  <div class="caption text-uppercase grey--text">{{ $t('billing-control.tabs.' + tab.key) }} <help-tip :text="$t('billing-control.help.tabs.' + tab.key)"/></div>
                  <div class="text-h6">{{ overview.revenueLeakage.counts[tab.key] }}</div>
                </v-card>
              </v-col>
            </v-row>

            <v-btn small color="primary" block class="mt-4" @click="goToGaps('missing-requests')">
              <v-icon left small>mdi-magnify</v-icon>
              {{ $t('billing-control.open-gap-analysis') }}
              <help-tip :text="$t('billing-control.help.open-gap-analysis')" dark/>
            </v-btn>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" lg="5">
        <v-card class="fill-height">
          <v-card-title class="text-subtitle-1">
            <v-icon left color="primary">mdi-lightbulb-on-outline</v-icon>
            {{ $t('billing-control.opportunities.title') }}
            <help-tip :text="$t('billing-control.help.opportunities.title')"/>
            <v-progress-circular v-if="opportunitiesLoading" indeterminate size="16" width="2" class="ml-2" color="primary"/>
          </v-card-title>
          <v-card-text>
            <v-row dense>
              <v-col cols="4">
                <div class="caption text-uppercase grey--text">{{ $t('billing-control.opportunities.assets-not-licensed') }} <help-tip :text="$t('billing-control.help.opportunities.assets-not-licensed')"/></div>
                <div class="text-h6">{{ opportunities ? opportunities.assets : '…' }}</div>
              </v-col>
              <v-col cols="4">
                <div class="caption text-uppercase grey--text">{{ $t('billing-control.opportunities.organizations') }} <help-tip :text="$t('billing-control.help.opportunities.organizations')"/></div>
                <div class="text-h6">{{ opportunities ? opportunities.organizations : '…' }}</div>
              </v-col>
              <v-col cols="4">
                <div class="caption text-uppercase grey--text">{{ $t('billing-control.opportunities.potential') }} <help-tip :text="$t('billing-control.help.opportunities.potential')"/></div>
                <div class="text-h6 primary--text">{{ opportunities ? numberFormat(opportunities.monthlyPotential) : '…' }} €</div>
              </v-col>
            </v-row>
            <v-btn small outlined color="primary" block class="mt-4" :to="{name: 'reports.revenue-opportunities', query: {year: selectedYear}}">
              {{ $t('billing-control.open-opportunity-analysis') }}
              <help-tip :text="$t('billing-control.help.open-opportunity-analysis')"/>
            </v-btn>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Expected vs Billed + Forecast -->
    <v-row>
      <v-col cols="12" lg="8">
        <v-card class="fill-height">
          <v-card-title class="text-subtitle-1">{{ $t('billing-control.expected-vs-billed.title') }} <help-tip :text="$t('billing-control.help.expected-vs-billed')"/></v-card-title>
          <v-card-text>
            <highcharts :options="expectedVsBilledOptions"></highcharts>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" lg="4">
        <v-card class="fill-height">
          <v-card-title class="text-subtitle-1">{{ $t('billing-control.forecast.title') }} <help-tip :text="$t('billing-control.help.forecast')"/></v-card-title>
          <v-card-text>
            <v-list dense>
              <v-list-item v-for="m in forecastMonths" :key="m.month">
                <v-list-item-content>
                  <v-list-item-title class="d-flex justify-space-between">
                    <span>{{ m.month }}</span>
                    <b>{{ numberFormat(m.total) }} €</b>
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    {{ numberFormat(m.annual) }} € {{ $t('billing-control.forecast.annual') }} ·
                    {{ numberFormat(m.monthly) }} € {{ $t('billing-control.forecast.monthly') }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Category Coverage -->
    <v-card class="mt-4">
      <v-card-title class="text-subtitle-1">{{ $t('billing-control.category-coverage.title') }} <help-tip :text="$t('billing-control.help.category-coverage.title')"/></v-card-title>
      <v-card-text>
        <v-simple-table dense>
          <thead>
            <tr>
              <th>{{ $t('billing-control.category-coverage.category') }}</th>
              <th class="text-right">{{ $t('billing-control.category-coverage.expected') }} <help-tip :text="$t('billing-control.help.category-coverage.expected')"/></th>
              <th class="text-right">{{ $t('billing-control.category-coverage.billed') }} <help-tip :text="$t('billing-control.help.category-coverage.billed')"/></th>
              <th class="text-right">{{ $t('billing-control.category-coverage.gap') }} <help-tip :text="$t('billing-control.help.category-coverage.gap')"/></th>
              <th class="text-center">{{ $t('billing-control.category-coverage.impacted-orgs') }} <help-tip :text="$t('billing-control.help.category-coverage.impacted-orgs')"/></th>
              <th>{{ $t('billing-control.category-coverage.coverage') }} <help-tip :text="$t('billing-control.help.category-coverage.coverage')"/></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in overview.categoryCoverage" :key="row.category" @click="goToGaps('missing-requests', {category: row.category})" style="cursor:pointer">
              <td>{{ categoryLabel(row.category) }}</td>
              <td class="text-right">{{ numberFormat(row.expected) }} €</td>
              <td class="text-right">{{ numberFormat(row.billed) }} €</td>
              <td class="text-right error--text">{{ numberFormat(row.gap) }} €</td>
              <td class="text-center">{{ row.impactedOrgs }}</td>
              <td style="min-width: 340px">
                <div class="coverage-row">
                  <v-tooltip bottom v-for="(pct, month) in row.monthlyCoverage" :key="month">
                    <template #activator="{ on, attrs }">
                      <div
                          class="coverage-cell"
                          :class="{'coverage-cell--current': month === currentMonth}"
                          :style="{backgroundColor: coverageColor(pct)}"
                          v-bind="attrs"
                          v-on="on"
                      >
                        <span class="coverage-month">{{ month.slice(5) }}</span>
                        <span class="coverage-value">{{ pct === null ? '·' : Math.round(pct) }}</span>
                      </div>
                    </template>
                    <span>{{ monthTooltip(month, pct) }}</span>
                  </v-tooltip>
                </div>
              </td>
            </tr>
          </tbody>
        </v-simple-table>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import BillingControlRepository from "@repository/BillingControlRepository"
import DashboardKpiCard from "@blocks/dashboard/DashboardKpiCard.vue"
import HelpTip from "./HelpTip.vue"
import { Chart } from "highcharts-vue"
import dayjs from "dayjs"

export default {
  components: {
    DashboardKpiCard,
    HelpTip,
    highcharts: Chart,
  },

  data() {
    return {
      overview: null,
      forecastMonths: [],
      opportunities: null,
      opportunitiesLoading: false,
      selectedYear: dayjs().year(),
      mode: 'cash',
      leakageTabs: [
        {key: 'missing-requests'},
        {key: 'quantity-issues'},
        {key: 'pricing-issues'},
      ],
    }
  },

  mounted() {
    this.loadData()
  },

  watch: {
    selectedYear() { this.loadData() },
    mode() { this.loadData() },
  },

  computed: {
    yearItems() {
      const current = dayjs().year()
      const years = []
      for (let y = current + 1; y >= current - 4; y--) {
        years.push(y)
      }
      return years
    },

    currentMonth() {
      return dayjs().format('YYYY-MM')
    },

    capturePercent() {
      const rate = this.overview && this.overview.kpis.revenueCaptureRate
      return rate === null || rate === undefined ? '—' : Math.round(rate * 100)
    },

    expectedVsBilledOptions() {
      const series = this.overview ? this.overview.expectedVsBilled : []
      const self = this
      return {
        chart: {type: 'column', height: 320},
        title: {text: null},
        credits: {enabled: false},
        xAxis: {categories: series.map(s => s.month)},
        yAxis: {title: {text: '€'}},
        tooltip: {
          shared: true,
          formatter() {
            const point = series[this.points[0].point.index]
            return `<b>${point.month}</b><br/>`
                + `${self.$t('billing-control.expected-vs-billed.expected')}: ${self.numberFormat(point.expected)} €<br/>`
                + `${self.$t('billing-control.expected-vs-billed.billed')}: ${self.numberFormat(point.billed)} €<br/>`
                + `${self.$t('billing-control.expected-vs-billed.gap')}: ${self.numberFormat(point.gap)} €`
          },
        },
        plotOptions: {
          series: {
            point: {
              events: {
                click() { self.goToGaps('missing-requests') },
              },
            },
          },
        },
        series: [
          {name: self.$t('billing-control.expected-vs-billed.expected'), data: series.map(s => s.expected), color: '#cbd5e1'},
          {name: self.$t('billing-control.expected-vs-billed.billed'), data: series.map(s => s.billed), color: '#0d9488'},
          {name: self.$t('billing-control.expected-vs-billed.gap'), data: series.map(s => s.gap), color: '#b91c1c'},
        ],
      }
    },
  },

  methods: {
    loadData() {
      this.showLoading(true)
      const data = {
        fromDate: `${this.selectedYear}-01-01`,
        toDate: `${this.selectedYear}-12-31`,
        mode: this.mode,
      }
      Promise.all([
        BillingControlRepository.overview(data),
        BillingControlRepository.forecast({}),
      ]).then(([overview, forecast]) => {
        this.overview = overview
        this.forecastMonths = forecast.months || []
        this.showLoading(false)
      }).catch(err => {
        this.notifyError(err)
        this.showLoading(false)
      })

      // Tuile Revenue Opportunities : chargée séparément (endpoint lourd, anomaly handler) pour ne
      // pas bloquer le rendu du dashboard principal.
      this.loadOpportunities()
    },

    loadOpportunities() {
      this.opportunitiesLoading = true
      BillingControlRepository.revenueOpportunities({limit: 1}).then(res => {
        this.opportunities = res.summary
        this.opportunitiesLoading = false
      }).catch(() => {
        this.opportunitiesLoading = false
      })
    },

    goToGaps(tab, extra = {}) {
      this.$router.push({
        name: 'reports.billing-gaps',
        query: {tab, year: this.selectedYear, ...extra},
      })
    },

    categoryLabel(category) {
      return category ? this.$t('enums.subscription-plan-types.' + category) : '—'
    },

    monthTooltip(month, pct) {
      const label = dayjs(month + '-01').format('MMMM YYYY')
      const coverage = pct === null || pct === undefined ? '—' : Math.round(pct) + '%'
      const suffix = month === this.currentMonth ? ' · ' + this.$t('billing-control.category-coverage.current-month') : ''
      return `${label} — ${coverage}${suffix}`
    },

    coverageColor(pct) {
      if (pct === null || pct === undefined) return '#e2e8f0'
      if (pct >= 95) return '#0d9488'
      if (pct >= 80) return '#f59e0b'
      return '#b91c1c'
    },
  },
}
</script>

<style lang="scss" scoped>
.coverage-row {
  display: flex;
  gap: 3px;
  width: 100%;
  margin: 8px 0;
}
.coverage-cell {
  flex: 1 1 0;
  min-width: 0;
  height: 34px;
  border-radius: 4px;
  color: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  line-height: 1.1;
}
.coverage-month {
  font-size: 8px;
  text-transform: uppercase;
  opacity: 0.85;
}
.coverage-value {
  font-size: 11px;
  font-weight: 600;
}
.coverage-cell--current {
  outline: 2px solid #191c1d;
  outline-offset: 1px;
}
</style>
